import { CustomButton } from "@/components/ui/Buttons";
import { Dispatch, SetStateAction, useState } from "react";
import LeftArrowShort from "@/assets/icons/authentication/LeftArrowShort.svg";
import Skip from "@/assets/icons/authentication/Skip.svg";

// outfit image
import FormalImgNational from "@/assets/images/auth/otufits/formal-national.png";
import SemiFormalImgNational from "@/assets/images/auth/otufits/semi-formal-natitonal.png";
import CasualImgNational from "@/assets/images/auth/otufits/casual-national.png";
import OtherImgNational from "@/assets/images/auth/otufits/others-national.png";

import FormalImgInternational from "@/assets/images/auth/otufits/formal-international.webp";
import SemiFormalImgInternational from "@/assets/images/auth/otufits/semi-formal-international.webp";
import CasualImgInternational from "@/assets/images/auth/otufits/casual-international.webp";
import OtherImgInternational from "@/assets/images/auth/otufits/other-international.webp";

// background images
import CleanWallImgInternational from "@/assets/images/auth/backgrounds/clean-wall-international.webp";
import CouchImgInternational from "@/assets/images/auth/backgrounds/couch-international.webp";
import IndoorImgInternational from "@/assets/images/auth/backgrounds/indoor-international.webp";
import OutdoorInternational from "@/assets/images/auth/backgrounds/outdoor-international.webp";

import CleanWallImgNational from "@/assets/images/auth/backgrounds/clean-wall-national.png";
import CouchImgNational from "@/assets/images/auth/backgrounds/couch-national.png";
import IndoorImgNational from "@/assets/images/auth/backgrounds/indoor-national.png";
import OutdoorNational from "@/assets/images/auth/backgrounds/outdoor-national.png";

import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import {
  resetApplicationForm,
  updateV2Field,
} from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";
import { handleApiRequest } from "@/utils/handleApiRequest";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useGetMeQuery } from "@/redux/feature/user/userApi";
import { useSubmitApplicationMutation } from "@/redux/feature/creatorApplication/creatorApplicationApi";
import { SkipToast } from "./Skiptoast";
import {
  scrollToFirstError,
  validateRequiredFields,
} from "@/utils/formValidationV2";
import { usePricingFormula } from "@/hooks/usePricingFormula";
import UploadOutfitBackground from "@/components/ui/UploadOutfitBackground";

type ApplicationStep6Props = {
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
};

const ApplicationStep6 = ({
  currentStep,
  setCurrentStep,
}: ApplicationStep6Props) => {
  const dispatch = useAppDispatch();
  const [showSkipToast, setShowSkipToast] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [outfitImagesUploading, setOutfitImagesUploading] = useState(false);
  const [backgroundImagesUploading, setBackgroundImagesUploading] =
    useState(false);
  const imagesUploading = outfitImagesUploading || backgroundImagesUploading;
  const { ucdT } = usePricingFormula();
  const isIndia = ucdT?.country === "IN";

  const outfits = isIndia
    ? [
        { name: "Formals", image: FormalImgNational },
        { name: "Semi-Formals", image: SemiFormalImgNational },
        { name: "Casuals", image: CasualImgNational },
        { name: "Others", image: OtherImgNational },
      ]
    : [
        { name: "Formals", image: FormalImgInternational },
        { name: "Semi-Formals", image: SemiFormalImgInternational },
        { name: "Casuals", image: CasualImgInternational },
        { name: "Others", image: OtherImgInternational },
      ];

  const backgrounds = isIndia
    ? [
        { name: "Clean Wall", image: CleanWallImgNational },
        { name: "Couch", image: CouchImgNational },
        { name: "Indoor", image: IndoorImgNational },
        { name: "Outdoor", image: OutdoorNational },
      ]
    : [
        { name: "Clean Wall", image: CleanWallImgInternational },
        { name: "Couch", image: CouchImgInternational },
        { name: "Indoor", image: IndoorImgInternational },
        { name: "Outdoor", image: OutdoorInternational },
      ];

  const { v2Extras } = useAppSelector((s) => s.signUpV2);

  const navigate = useNavigate();

  const { SubmitApplicationInfo } = useAppSelector((s) => s.signUpV2);
  const { user } = useAppSelector((state) => state.auth);

  // The application API authenticates the request with the access token. Fetching
  // the user record here is only a best-effort way to include `user_id`; it must
  // never prevent a verified creator from submitting their application.
  const { data, isLoading: isAccountLoading } = useGetMeQuery(
    user?.email ?? "",
    { skip: !user?.email },
  );
  const userId = data?.data?._id;

  const [submitApplication, { isLoading }] = useSubmitApplicationMutation();

  const mapTaggedFile = (x: any) => ({
    name: x.name,
    size: x.size,
    type: x.type,
    live_url: x.live_url,
    hash: x.hash,
    tag: x.tag ?? "",
    otherTagText: x.otherTagText ?? "",
  });

  const transformFormData = (data: any) => ({
    email: user?.email,
    fullName: data.fullName,
    gender: data.gender,
    age: Number(data.age),
    phoneNumber: data.phoneNumber,
    country: data.country,
    city: data.city,
    state: data.state,
    addressWithZipCode: data.address,
    skinTone: data.skinTone,
    bodyType: data.bodyType,
    hasRelativesInVideo: data.hasRelativesInVideo,
    user_id: userId,
    brandsDontWantToWorkWith: data.brandsDontWantToWorkWith,
    profileLink: data.profileLink,
    experience: data.experience,
    experienceDescription: data.experienceDescription,
    videoLinks: data.videoLinks,
    topics: data.topics,
    skills: data.skills,
    availableHoursPerWeek: data.availableHoursPerWeek,
    availabilityForWeekendsOrEvenings: data.weekendAvailability,
    languagesSpoken: data.language,
    lastDrawnSalary: data.lastSalary ? `${data.lastSalary}` : "",
    expectedPay: data.expectedPay ? `${data.expectedPay}` : "",
    wardrobeImages: data.wardrobeStyle,
    backgroundImages: data.backgroundStyle,
    videoEquipment: {
      propsAvailable: data?.propsAvailable,
      greenScreenSpecs: data.greenScreenDetails,
      noiselessRoomAndMicrophoneSpecs: data.audioEquipmentDetails,
      cameraOrSmartphoneSpecs: data.cameraDetails,
      lightingSpecs: data.lightingDetails,
    },
    additionalInfo: data.additionalExperience,
  });

  const clearFieldError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  const validateStep = () => {
    const formValues = {
      wardrobeImages: v2Extras.wardrobeImages,
      backgroundImages: v2Extras.backgroundImages,
    };

    const validationErrors = validateRequiredFields(formValues, [
      "wardrobeImages",
      "backgroundImages",
    ]);

    if (!v2Extras.wardrobeImages || v2Extras.wardrobeImages.length < 3) {
      validationErrors.wardrobeImages =
        "Please upload at least 3 outfit options";
    }

    if (!v2Extras.backgroundImages || v2Extras.backgroundImages.length < 3) {
      validationErrors.backgroundImages =
        "Please upload at least 3 background options";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      scrollToFirstError(validationErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmitApplication = async () => {
    if (!validateStep()) return;

    if (!user?.email) {
      toast.error("Your session has expired. Please sign in and try again.");
      return;
    }

    if (isAccountLoading) {
      toast.error(
        "Finishing your account setup. Please try again in a moment.",
      );
      return;
    }

    const imageFiles = [
      ...(v2Extras.profilePhoto ? [v2Extras.profilePhoto] : []),
      ...(v2Extras.wardrobeImages ?? []),
      ...(v2Extras.backgroundImages ?? []),
    ];
    const failedUploads = imageFiles.some(
      (file) => "uploadError" in file && file.uploadError,
    );
    const pendingUploads = imageFiles.some((file) => !file.live_url);

    if (failedUploads) {
      toast.error("Some images failed to upload. Retry or remove them first.", {
        id: "application-image-upload-error",
      });
      return;
    }

    if (pendingUploads) {
      toast.error("Please wait for all images to finish uploading.", {
        id: "application-images-uploading",
      });
      return;
    }

    const cleanedProfileLinks = (v2Extras.profileLinks ?? []).filter(
      (item) => item.url && item.url.trim() !== "",
    );

    const transformedData = transformFormData(SubmitApplicationInfo);

    const payload = {
      ...transformedData,
      ...v2Extras,
      // Keep these values in the shape persisted by the application schema.
      pinCode: v2Extras.pincode,
      languagesSpoken: v2Extras.languages
        .map((item) => item.name.trim())
        .filter(Boolean),
      lastDrawnSalary: v2Extras.lastDrawnSalary,
      expectedPay: `${v2Extras.expectedPayout}${
        v2Extras.expectedPayoutType ? ` (${v2Extras.expectedPayoutType})` : ""
      }`,
      wardrobeImages: (v2Extras.wardrobeImages ?? []).map(mapTaggedFile),
      profilePhoto: v2Extras.profilePhoto?.live_url,
      backgroundImages: (v2Extras.backgroundImages ?? []).map(mapTaggedFile),
      profileLinks: cleanedProfileLinks,
      skills: [
        ...(v2Extras.skillsMulti ?? []),
        ...(v2Extras.customSkills ?? []),
      ].join(", "),
    };

    console.log(payload);

    await handleApiRequest({
      payload: payload,
      requestFunction: submitApplication,
      onSuccess: () => {
        toast.success("Application submitted successfully!");
        dispatch(resetApplicationForm());
        navigate("/dashboard");
      },
      onError: (err) => {
        const error = err as {
          data?: { errorSource?: { path?: string; message: string }[] };
        };

        if (error?.data?.errorSource?.length) {
          error.data.errorSource.forEach((item) => {
            toast.error(item.message);
          });
        } else {
          const message = (err as { data?: { message?: string } })?.data
            ?.message;
          toast.error(message || "Submission failed. Please try again.");
        }
      },
    });
  };

  return (
    <>
      <div>
        {/* header */}
        <div className="mb-8">
          <div className="flex items-center gap-x-2 ">
            <div className="bg-[#FF6600] px-[8.5px] rounded-[20px]">
              <p className="text-white text-[11px] leading-5 tracking-normal">
                Step 5 of 5
              </p>
            </div>
            <h1 className="text-[#060B13] text-xl tracking-[-0.7px] font-medium">
              Outfits & Backgrounds
            </h1>
          </div>
          <p className="text-[#475467] text-xs tracking-[-0.5%] mt-2">
            Add outfit and background options you already have. This helps
            brands plan shoots faster.
          </p>
        </div>

        <div className="space-y-8">
          <div id="wardrobeImages">
            <h6 className="tracking-normal text-sm">
              <span className="text-[#121217] font-medium">
                Add outfit options <span className="text-red-500">*</span>
              </span>
              <span className="text-[#475467]">
                {" "}
                (Upload 3-4 images of outfits you can wear on camera.)
              </span>
            </h6>

            <p className="text-[#475467] text-[10px] tracking-normal mt-1">
              Formals, business casual, casuals, traditional — phone photos work
              fine.
            </p>

            {/* Samples */}
            <div className="gap-2 mb-5 mt-5">
              <p className="text-[#475467] text-[10px] tracking-normal mb-2">
                Samples :
              </p>
              <div className="flex gap-4">
                {outfits.map((outfit) => (
                  <div key={outfit.name} className="flex flex-col items-center">
                    <div className="w-[66px] lg:w-[75px] mb-1 relative">
                      <img
                        src={outfit.image}
                        alt={outfit.name}
                        className="object-cover rounded-lg"
                      />
                    </div>
                    <div className="bg-[#F0F0F0] py-1 px-1 rounded w-[66px] lg:w-[75px]">
                      <p className="text-[10px] text-[#475467] tracking-normal text-center">
                        {outfit.name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <UploadOutfitBackground
              mode="outfit"
              useExternalState
              externalFiles={v2Extras.wardrobeImages ?? []}
              onExternalFilesChange={(files) => {
                dispatch(updateV2Field("wardrobeImages", files));
                if (files?.length > 0) clearFieldError("wardrobeImages");
              }}
              useLiveUrl
              onUploadingChange={setOutfitImagesUploading}
            />
            {errors.wardrobeImages && (
              <p className="text-red-500 text-xs mt-1">
                {errors.wardrobeImages}
              </p>
            )}
          </div>

          <div id="backgroundImages">
            <h6 className="tracking-normal text-sm">
              <span className="text-[#121217] font-medium">
                Add background options <span className="text-red-500">*</span>
              </span>
              <span className="text-[#475467]">
                {" "}
                (Upload 3-4 images of backgrounds you already have.)
              </span>
            </h6>

            <p className="text-[#475467] text-[10px] tracking-normal mt-1">
              Simple wall, aesthetic setup, couch, kitchen, balcony, greenery.
            </p>

            {/* Samples */}
            <div className="gap-2 mb-5 mt-5">
              <p className="text-[#475467] text-[10px] tracking-normal mb-2">
                Samples :
              </p>
              <div className="flex gap-4">
                {backgrounds.map((background) => (
                  <div
                    key={background.name}
                    className="flex flex-col items-center"
                  >
                    <div className="mb-1 relative">
                      <img
                        src={background.image}
                        alt={background.name}
                        className="object-cover rounded-lg h-[100px] lg:h-[130px]"
                      />
                    </div>
                    <div className="bg-[#F0F0F0] py-1 px-1 rounded w-[66px] lg:w-[75px]">
                      <p className="text-[10px] text-[#475467] tracking-normal text-center">
                        {background.name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <UploadOutfitBackground
              mode="background"
              useExternalState
              externalFiles={v2Extras.backgroundImages ?? []}
              onExternalFilesChange={(files) => {
                dispatch(updateV2Field("backgroundImages", files));
                if (files?.length > 0) clearFieldError("backgroundImages");
              }}
              useLiveUrl
              onUploadingChange={setBackgroundImagesUploading}
            />
            {errors.backgroundImages && (
              <p className="text-red-500 text-xs mt-1">
                {errors.backgroundImages}
              </p>
            )}
          </div>
        </div>

        {/* action button */}
        <div className="mt-[50px] flex items-center justify-end gap-1 md:gap-2 lg:gap-3">
          <CustomButton
            variant="outline"
            border="1px solid #E4E4E4"
            fontSize="14px"
            fontWeight="600"
            color="#4D4D4D"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px] flex items-center gap-x-1.5"
            onClick={() => setCurrentStep((prev) => prev - 1)}
          >
            <LeftArrowShort />
            Go Back
          </CustomButton>

          <CustomButton
            backgroundColor="#FF6600"
            fontSize="14px"
            fontWeight="600"
            color="white"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px]"
            onClick={handleSubmitApplication}
            disabled={isLoading || imagesUploading}
          >
            {isLoading
              ? "Submitting..."
              : imagesUploading
                ? "Uploading Images..."
                : "Submit Application"}
          </CustomButton>
        </div>
      </div>

      <SkipToast
        visible={showSkipToast}
        onClose={() => setShowSkipToast(false)}
      />
    </>
  );
};

export default ApplicationStep6;
